import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';

import Button from '../components/Button';
import Hero from '../components/Hero';
import SectionHero from '../components/SectionHero';
import IndustryList from '../components/IndustryList';
import ContentBlock from '../components/ContentBlock';
import TestimonialCard from '../components/TestimonialCard';
import USPGrid from '../components/USPGrid';
import CardGrid from '../components/CardGrid';
import { DefaultLogoReel } from '../components/LogoReel';
import Section from '../components/Section';

const ModernizingBusinessBaseTemplate = ({ pageContext: { page } }) => {
  const { usps, contentBlocks } = page;

  const { caseStudies, resources } = useStaticQuery(graphql`
    fragment authorData on GraphCMS_Author {
      name
      title
      bio
      photo {
        ...assetData
      }
    }

    fragment assetData on GraphCMS_Asset {
      id
      url
      handle
      width
      height
    }

    fragment resourceData on GraphCMS_Resource {
      id
      slug
      friendlySlug
      friendlyStartDate
      title
      subTitle
      truncatedSubTitle
      coverImage {
        ...assetData
      }
      speakers {
        ...authorData
      }
      content
      externalUrl
      location
      starts
      type
      linkPath
    }

    fragment caseStudyData on GraphCMS_CaseStudy {
      id
      slug
      friendlySlug
      title
      subTitle
      coverImage {
        ...assetData
      }
      excerpt
      hasContent
      content
      externalUrl
      company {
        name
        url
        logo {
          ...assetData
        }
      }
      customer {
        ...authorData
      }
      linkPath
    }

    {
      resources: allGraphCmsResource(
        filter: {
          remoteId: {
            in: ["ckc98ju5c08cp01078vlj1x2n", "ckc7ygv5k0ihn0154ti5oj00e"]
          }
        }
      ) {
        nodes {
          ...resourceData
        }
      }

      caseStudies: allGraphCmsCaseStudy(
        filter: {
          remoteId: {
            in: [
              "ckgs1j1b400rw0a044fk0hpys"
              "ck5nsgxtt0avy0b662swgflk5"
              "ckgi5llcw00d00b07yoi6k8sb"
            ]
          }
        }
      ) {
        nodes {
          ...caseStudyData
        }
      }
    }
  `);

  return (
    <React.Fragment>
      <Section>
        <Hero {...page} alignment="left">
          <Button
            href="/contact"
            appearance="outline"
            colour="pink"
            size="large"
          >
            Get an enterprise demo &#8594;
          </Button>
        </Hero>
      </Section>
      <DefaultLogoReel />
      <Section>
        <SectionHero
          title="Break down your silos with content federation"
          description="Time matters when you're delivering content at an ambitious scale. GraphCMS provides you with central control of your enterprise content from multiple sources through a single, unified content layer."
        />

        <USPGrid title={false} items={usps} />
      </Section>
      <Section>
        <SectionHero
          title="Business critical content management on your terms"
          description="Unleash the power of structured content from a central hub. Move faster with a cloud based content platform and modernize your legacy stack."
        />
        <IndustryList entries={contentBlocks} Component={ContentBlock} />
      </Section>
      <Section colour="light">
        <SectionHero
          title="See how companies like yours are redefining the way in which they manage and deliver their content"
          description="Global businesses operating at a global scale rely on GraphCMS to deliver their digital experiences in a programmatic and effective manner across borders"
          ctas={[
            { href: '/case-studies', label: 'View customer success stories' },
          ]}
        />
        <IndustryList entries={caseStudies.nodes} Component={TestimonialCard} />
      </Section>
      <Section>
        <SectionHero
          title="Explore our resources to learn how GraphCMS can accelerate your modernization"
          description="Get up to speed with implementing a scalable content architecture with our carefully crafted studies and experiences"
          ctas={[{ href: '/resources', label: 'Download GraphCMS resources' }]}
        />
        <CardGrid entries={resources.nodes} />
      </Section>
    </React.Fragment>
  );
};

export default ModernizingBusinessBaseTemplate;
